/**
 * <TutorLiveActivityView> — l'activité d'un élève, regardée au centre du live du masteur (organism).
 *
 * Les vues de l'ancienne fiche en live (`TodoModal`, `noModal`), sur les pièces du centre Énoncé +
 * Rendu de l'élève et du parent :
 *  - `statement` : l'énoncé (`<ActivityStatement captionCut>` : le texte d'un énoncé non écrit en
 *    sous-titre, coupé au-delà de 100 caractères et déplié au toucher, comme `MaterialDisplayModal`) ;
 *  - `work` : le rendu (`<ActivityWork fill>` — la copie à pastilles quand Sam a corrigé), ou « Pas
 *    encore de rendu » tant qu'il n'a rien à montrer (`activityWorkShows`) ; sur une correction du champion,
 *    l'avis du masteur (`<TutorCorrectionReview>`, le même que dans la fiche) ;
 *  - `split` : les deux côte à côte (la bascule « Côte à côte » du centre), l'énoncé cédant de la
 *    place à une copie à pastilles ;
 *  - `quiz` : le quiz de prérequis de l'activité, la même relecture que la fiche (`<TutorQuizReview>`),
 *    avec l'avis du masteur et « Signaler » ;
 *  - `whiteboard` : le tableau blanc, la plus grande surface possible, et sa barre posée dessus — l'encre
 *    et la gomme (`handlers.whiteboard`) ; le visuel de Sam, demandé (« Générer un visuel »), en
 *    préparation, là (montré ou caché, et l'avis du masteur) ou indisponible.
 *
 * **Le contenu d'abord** (round 3) : aucune ligne au-dessus de lui. Le titre « Rendu + correction » est
 * parti, les commandes de la copie sont posées sur la page (`<ActivityWork stage>`), l'avis sur la
 * correction se donne sous elle.
 *
 * **Pendant une présentation**, les élèves voient le centre : le rendu s'y montre nu, sans correction ni
 * avis (`presenting`, l'ancienne `TodoModal` faisait de même) ; rien de ce qui est au masteur seul ne s'y
 * pose (la barre du tableau, la bascule du visuel et son avis : la barre de dessin est alors dans la ligne
 * du centre, hors de ce qui est partagé, et `<TutorLiveCenter>` y passe la bascule et l'avis —
 * `presentationVisual`). Un rendu qui n'a que sa correction s'y dit « Pas encore de rendu ».
 *
 * **Un calque de dessin par surface** (`annotation`) : l'énoncé, le rendu et le tableau ont chacun le leur,
 * posé sur leur contenu, comme l'ancien moteur (un canevas et son annulation par surface). La vue ne
 * dessine rien : le moteur rend son canevas dans la fente.
 *
 * Chaque colonne défile seule ; la page tient la hauteur. Contrôlé : l'état de chaque avis et du visuel
 * arrive avec l'activité (`focus`), les gestes repartent par `handlers`.
 */
import React from "react"
import { Image, ScrollView, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UICorrectionFeedback } from "../../../ports/primary/ui/types/studentActivity"
import { type UITutorLiveFocus } from "../../../ports/primary/ui/types/tutorLive"
import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { WaitingText } from "../../atoms/WaitingText"
import { Card } from "../../molecules/Card"
import { EmptyState } from "../../molecules/EmptyState"
import { CorrectionFeedback } from "../../molecules/CorrectionFeedback"
import { TutorCorrectionReview } from "../../molecules/TutorCorrectionReview"
import { ActivityStatement } from "../ActivityStatement"
import { ActivityWork, type ActivityWorkAudio } from "../ActivityWork"
import { activityWorkShows } from "../ActivityWork/activityWorkContent"
import { TutorAnnotationToolbar, type TutorAnnotationPen } from "../../molecules/TutorAnnotationToolbar"
import { TutorQuizReview } from "../TutorQuizReview"
import { TutorStatementAid } from "../../molecules/TutorStatementAid"

/** Les gestes de l'activité regardée. Absent : pas de bouton, pas d'avis à donner. */
export type TutorLiveActivityHandlers = {
    /** « Générer un visuel » (et le relancer quand il n'a pas pu être fait). */
    onGenerateVisual?: () => void
    /** « Visuel » : le montrer ou le cacher sur le tableau. */
    onToggleVisual?: () => void
    onVisualFeedback?: (feedback: UICorrectionFeedback) => void
    onCorrectionReview?: (review: UICorrectionFeedback) => void
    onQuizFeedback?: (feedback: UICorrectionFeedback) => void
    /** « Signaler » une question du quiz (l'index de la question). */
    onSignalQuestion?: (index: number) => void
    /** L'écoute d'un rendu oral (l'ancienne fiche le jouait). Absent : la ligne dit seulement qu'il existe. */
    audio?: ActivityWorkAudio
    /** L'aide à la correction de l'énoncé : l'ouvrir ou la replier, la relancer, l'avis du masteur. */
    onToggleAid?: () => void
    onRetryAid?: () => void
    onAidFeedback?: (feedback: UICorrectionFeedback) => void
    /** La barre du tableau blanc : l'encre, la gomme, annuler, tout effacer. Absente : pas de barre. */
    whiteboard?: { pen: TutorAnnotationPen; canUndo?: boolean; onUndo: () => void; onClear: () => void }
}

/** Les surfaces où l'on dessine, chacune avec son calque. */
export type TutorLiveDrawSurface = "statement" | "work" | "whiteboard"

/** Ce que la vue montre : un onglet de l'activité, ou l'énoncé et le rendu côte à côte. */
export type TutorLiveActivityViewKind = "statement" | "work" | "split" | "quiz" | "whiteboard"

export type TutorLiveActivityViewProps = {
    focus: UITutorLiveFocus
    view: TutorLiveActivityViewKind
    handlers?: TutorLiveActivityHandlers
    /** Le partage d'écran tourne : le rendu se montre sans correction. Défaut `false`. */
    presenting?: boolean
    /** Le calque de dessin d'une surface (le canevas du moteur). Absent : rien par-dessus. */
    annotation?: (surface: TutorLiveDrawSurface) => React.ReactNode
    /**
     * Le centre a la place d'une copie entière (`<ActivityWork fill>`, son point à côté). Sinon (un
     * téléphone), le rendu défile, la page en largeur et son point dessous. Défaut `true`.
     */
    roomy?: boolean
    testID?: string
}

const TEXTS = {
    noWork: "Pas encore de rendu",
    noWorkMessage: "Sa photo arrivera ici dès qu'il l'enverra.",
    generateVisual: "Générer un visuel",
    generating: "Sam prépare le visuel…",
    visual: "Visuel",
    visualAlt: "Le visuel de Sam",
    unavailable: "Visuel non disponible",
    retry: "Réessayer",
    visualFeedback: { question: "Ce visuel t'aide ?", yes: "Utile", no: "Pas utile" },
}

export function TutorLiveActivityView(props: TutorLiveActivityViewProps) {
    const { focus, view, handlers = {}, presenting = false, roomy = true, annotation, testID } = props
    const layer = (surface: TutorLiveDrawSurface) =>
        annotation != null ? (
            <View style={styles.layer} pointerEvents="box-none">
                {annotation(surface)}
            </View>
        ) : null

    const statement = (
        <ScrollView style={styles.flex} contentContainerStyle={styles.pad}>
            <Card p="4">
                {/* Le texte d'une photo, d'un PDF ou d'un lien : coupé, déplié au toucher (l'ancien centre). */}
                <ActivityStatement statement={focus.statement} captionCut />
                {layer("statement")}
            </Card>
            {/* L'aide à la correction, au masteur seul : jamais devant les élèves (une présentation). */}
            {focus.statementAid != null && !presenting && (
                <View style={styles.aid}>
                    <TutorStatementAid
                        aid={focus.statementAid}
                        onToggle={handlers.onToggleAid}
                        onRetry={handlers.onRetryAid}
                        onFeedback={handlers.onAidFeedback}
                    />
                </View>
            )}
        </ScrollView>
    )

    // L'avis sur la correction de Sam : sur une correction, si l'écran le demande, et jamais devant les élèves.
    const onCorrectionReview = handlers.onCorrectionReview
    const review =
        !presenting &&
        focus.work?.hasCorrection === true &&
        focus.correctionReview !== undefined &&
        onCorrectionReview != null ? (
            <Card p="3" background="surface-subtle" elevation="none">
                <TutorCorrectionReview value={focus.correctionReview} onChange={onCorrectionReview} />
            </Card>
        ) : null

    // Rien à montrer, « Pas encore de rendu » : pas de rendu, un rendu annoncé dont rien ne se lit (aucune
    // photo retrouvée, un oral sans son fichier — `<ActivityWork>` ne rendait rien, sa carte restait vide), ou,
    // pendant une présentation, la correction seule (la copie s'y montre nue).
    const hasWork = activityWorkShows(focus.work, presenting)
    const work =
        hasWork && focus.work != null ? (
            roomy ? (
                <View style={[styles.flex, styles.pad, styles.gap]}>
                    <Card p="3" style={styles.flex}>
                        <ActivityWork work={focus.work} fill stage hideCorrection={presenting} audio={handlers.audio} />
                        {layer("work")}
                    </Card>
                    {review}
                </View>
            ) : (
                <ScrollView style={styles.flex} contentContainerStyle={[styles.pad, styles.gap]}>
                    <Card p="3">
                        <ActivityWork work={focus.work} stage hideCorrection={presenting} audio={handlers.audio} />
                        {layer("work")}
                    </Card>
                    {review}
                </ScrollView>
            )
        ) : (
            <View style={[styles.flex, styles.center]}>
                <EmptyState title={TEXTS.noWork} message={TEXTS.noWorkMessage} iconName="camera" />
            </View>
        )

    const visual = focus.visual
    const whiteboardCanvas =
        visual == null ? (
            <Card p="4" style={[styles.flex, styles.center]}>
                {handlers.onGenerateVisual != null && (
                    <Button
                        label={TEXTS.generateVisual}
                        iconName="magic"
                        variant="outline"
                        onPress={handlers.onGenerateVisual}
                    />
                )}
            </Card>
        ) : visual.state === "generating" ? (
            <Card p="4" style={[styles.flex, styles.center]}>
                <WaitingText>{TEXTS.generating}</WaitingText>
            </Card>
        ) : visual.state === "unavailable" ? (
            <Card p="4" style={[styles.flex, styles.center]}>
                <Stack gap="3" align="center">
                    <Banner message={TEXTS.unavailable} icon="info-circle" color="text-muted" align="center" />
                    {handlers.onGenerateVisual != null && (
                        <Button
                            label={TEXTS.retry}
                            iconName="redo"
                            variant="outline"
                            size="sm"
                            onPress={handlers.onGenerateVisual}
                        />
                    )}
                </Stack>
            </Card>
        ) : (
            <Card p="3" style={styles.flex}>
                <View style={styles.canvas}>
                    {visual.shown && (
                        <Image
                            source={{ uri: visual.uri }}
                            style={styles.visual}
                            resizeMode="contain"
                            accessibilityLabel={TEXTS.visualAlt}
                        />
                    )}
                </View>
            </Card>
        )

    let body: React.ReactNode
    switch (view) {
        case "statement":
            body = statement
            break
        case "work":
            body = work
            break
        case "split": {
            const interactive = focus.work?.copyPages != null && focus.work.correctionPoints != null
            body = (
                <View style={styles.split}>
                    <View style={styles.column(interactive ? 2 : 1)}>{statement}</View>
                    <View style={styles.column(interactive ? 3 : 1)}>{work}</View>
                </View>
            )
            break
        }
        case "quiz": {
            const onQuizFeedback = handlers.onQuizFeedback
            body = (
                <ScrollView style={styles.flex} contentContainerStyle={[styles.pad, focus.quiz == null && styles.grow]}>
                    <TutorQuizReview
                        review={focus.quiz ?? null}
                        feedback={
                            focus.quizFeedback !== undefined && onQuizFeedback != null
                                ? { value: focus.quizFeedback, onChange: onQuizFeedback }
                                : null
                        }
                        onSignalQuestion={handlers.onSignalQuestion}
                        compact
                    />
                </ScrollView>
            )
            break
        }
        case "whiteboard": {
            const board = handlers.whiteboard
            body = (
                <View style={[styles.flex, styles.pad]}>
                    <View style={styles.flex}>
                        {whiteboardCanvas}
                        {layer("whiteboard")}
                        {/* La barre et le visuel posés sur le tableau : aucune ligne au-dessus de lui. */}
                        {board != null && !presenting && (
                            <View style={styles.boardTools}>
                                <Card p="1" elevation="floating">
                                    <TutorAnnotationToolbar
                                        pen={board.pen}
                                        canUndo={board.canUndo}
                                        onUndo={board.onUndo}
                                        onClear={board.onClear}
                                    />
                                </Card>
                            </View>
                        )}
                        {visual?.state === "ready" && !presenting && (
                            <View style={styles.visualTools}>
                                <Card p="1" elevation="floating">
                                    <Row gap="2" align="center">
                                        <ChoiceChip
                                            label={TEXTS.visual}
                                            iconName={visual.shown ? "eye" : "eye-slash"}
                                            selected={visual.shown}
                                            onPress={() => handlers.onToggleVisual?.()}
                                        />
                                        {visual.feedback !== undefined && handlers.onVisualFeedback != null && (
                                            <CorrectionFeedback
                                                value={visual.feedback}
                                                onChange={handlers.onVisualFeedback}
                                                texts={TEXTS.visualFeedback}
                                            />
                                        )}
                                    </Row>
                                </Card>
                            </View>
                        )}
                    </View>
                </View>
            )
            break
        }
    }

    return (
        <View style={styles.flex} testID={testID}>
            {body}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    flex: { flex: 1, minHeight: 0 },
    pad: { padding: theme.spacing["1"], paddingBottom: theme.spacing["4"] },
    gap: { gap: theme.spacing["3"] },
    aid: { paddingTop: theme.spacing["2"], paddingHorizontal: theme.spacing["1"] },
    center: { alignItems: "center" as const, justifyContent: "center" as const },
    split: { flex: 1, minHeight: 0, flexDirection: "row", gap: theme.spacing["3"] },
    column: (weight: number) => ({ flex: weight, minWidth: 0, minHeight: 0 }),
    grow: { flexGrow: 1, justifyContent: "center" as const },
    /** Un calque de dessin : toute la surface, par-dessus son contenu. */
    layer: { position: "absolute" as const, top: 0, left: 0, right: 0, bottom: 0 },
    boardTools: { position: "absolute" as const, top: theme.spacing["2"], left: theme.spacing["2"] },
    visualTools: { position: "absolute" as const, bottom: theme.spacing["2"], right: theme.spacing["2"] },
    /** Le tableau : blanc, le visuel posé dessus quand il est montré. */
    canvas: { flex: 1, minHeight: 200, alignItems: "center" as const, justifyContent: "center" as const },
    visual: { width: "100%", height: "100%" },
}))
